import React, { useState } from 'react';
import '../styles.css';
import './Experience.css';

const Experience = () => {
    const [openDropdowns, setOpenDropdowns] = useState({});

    const toggleDropdown = (itemId) => {
        setOpenDropdowns(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    // Function to get favicon URL
    const getFaviconUrl = (url) => {
        try {
            const domain = new URL(url).hostname;
            return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
        } catch {
            return null;
        }
    };

    // Function to get webpage screenshot/preview
    const getWebpagePreview = (url) => {
        const encoded = encodeURIComponent(url);
        return `https://api.microlink.io/?url=${encoded}&screenshot=true&embed=image.url`;

    };


    // Experience data with additional info
    const experiences = [
        {
            id: 'ibm',
            logo: '/images/ibm.png',
            company: 'IBM',
            position: 'AI Compiler Engineer',
            dates: '2025 – Present',
            location: 'Waterford, Ireland',
            description: [
                'Developing the AI compiler and runtime infrastructure for the Spyre AI Accelerator.',
                'Working on torch-spyre backend, optimising symbolic execution, graph decomposition & physical memory management.',
                'Lead the open-source initiative to upstream s390x architecture support for Numba, enhancing the scientific computing stack for IBM Z.'
            ],
            moreInfo: {
                links: [
                    {
                        title: 'IBM z17 Mainframe',
                        url: 'https://www.ibm.com/products/z17',
                        description: 'Latest generation IBM Mainframe'
                    },
                    {
                        title: 'IBM Telum II and Spyre AI Accelerator',
                        url: 'https://www.ibm.com/new/announcements/telum-ii',
                        description: 'IBM’s latest mainframe processor with integrated AI acceleration and the Spyre PCIe AI card.',
                        previewImage: 'https://research.ibm.com/_next/image?url=https%3A%2F%2Fresearch-website-prod-cms-uploads.s3.us.cloud-object-storage.appdomain.cloud%2FBlog_Tellum_With_Spyre_Still_72e48003f3.jpg&w=1080&q=75'
                    }
                ]
            }
        },
        {
            id: 'adi',
            logo: '/images/adi.png',
            company: 'Analog Devices',
            position: 'Embedded Software Engineer',
            dates: '2023 – 2025',
            location: 'Limerick, Ireland',
            description: [
                'Developed embedded software for Analog Devices’ Battery Management System Emulator, a real-time digital twin platform used by Automotive OEMs.',
                'Designed heterogeneous-computing tools and platforms to support embedded machine-learning for multiprocessor microcontrollers.',
                'Contributed to HPC frameworks for battery modeling, supporting my Bachelor’s final year project on lithium-ion battery simulation.',
                'Awarded Academic Excellence Scholarship in 2nd year of university; worked year-round during studies.'
            ],
            moreInfo: {
                links: [
                    {
                        title: 'ADI Wireless Battery Management System (wBMS)',
                        url: 'https://www.analog.com/en/product-category/wireless-battery-management-systems.html',
                        description: 'Developer of the ADI wBMS Emulator, an Embedded System Digital Twin',
                        previewImage: 'https://i.ytimg.com/vi/dVL9DaxUQY0/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLCEO0X2J_NdNRva-FgUkxlIMlcz7Q'
                    },
                    {
                        title: 'Analog Devices MSDK',
                        url: 'https://developer.analog.com/solutions/msdk',
                        description: 'Open-source SDK and toolchain for Analog Devices microcontrollers. Developed dual-core support for the MAX32666 platform.'
                    },
                    {
                        title: 'Analog Devices Battery Modelling Framework',
                        url: 'https://www.comsol.com/video/keynote-battery-insights-at-the-intelligent-edge-enabled-by-analog-devices-precision-signal-chain-and-physics-based-modeling',
                        description: 'A keynote from the team I worked with at Analog Devices, covering our work on battery modeling frameworks for battery model development, with the goal of customer tailored algorithm development pipelines.',
                        previewImage: './images/adi_keynote.png'

                    },
                    {
                        title: 'Analog Devices x JLR',
                        url: 'https://www.analog.com/en/signals/videos/driving-automotive-innovation-jlr.html',
                        description: 'ADI and JLR co-creation partnership',
                        previewImage: 'https://cf-images.us-east-1.prod.boltdns.net/v1/static/706011717001/316be89c-95e2-48d9-bb08-e79b40344b9b/20bc1328-69e6-4ee6-b43f-545e27c55b99/1280x720/match/image.jpg'
                    }
                ]
            }
        }
    ];

    return (
        <div className="center-container">
            <div className="content-section">
                <h1>Experience</h1>

                {experiences.map((exp) => (
                    <div key={exp.id} className="content-section">
                        <div className="experience-item">
                            <div className="experience-content">
                                <div className="company-logo">
                                    <img src={exp.logo} alt={exp.company} />
                                </div>
                                <div className="experience-info">
                                    <div className="job-header">
                                        <h2>{exp.position}</h2>
                                        <span className="job-dates">{exp.dates}</span>
                                    </div>
                                    <p className="company-location">{exp.company} • {exp.location}</p>
                                    {exp.description.map((paragraph, index) => (
                                        <p key={index}>{paragraph}</p>
                                    ))}

                                    <button
                                        className="more-info-toggle"
                                        onClick={() => toggleDropdown(exp.id)}
                                        aria-expanded={openDropdowns[exp.id] || false}
                                    >
                                        More Info
                                        <svg
                                            className={`dropdown-arrow ${openDropdowns[exp.id] ? 'rotated' : ''}`}
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                        >
                                            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                                        </svg>
                                    </button>

                                    <div className={`more-info-dropdown ${openDropdowns[exp.id] ? 'open' : ''}`}>
                                        <div className="more-info-content">
                                            <div className="links-grid">
                                                {exp.moreInfo.links.map((link, index) => {
                                                    const previewImage = link.previewImage || getWebpagePreview(link.url);
                                                    const faviconUrl = getFaviconUrl(link.url);

                                                    return (
                                                        <a
                                                            key={index}
                                                            href={link.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="link-card"
                                                        >
                                                            {previewImage && (
                                                                <div className="link-image">
                                                                    <img src={previewImage} alt={link.title} />
                                                                </div>
                                                            )}
                                                            <div className="link-content">
                                                                <div className="link-header">
                                                                    {faviconUrl && (
                                                                        <img
                                                                            src={faviconUrl}
                                                                            alt=""
                                                                            className="link-favicon"
                                                                            onError={(e) => e.target.style.display = 'none'}
                                                                        />
                                                                    )}
                                                                    <span className="link-title">{link.title}</span>
                                                                    <svg width="12" height="12" viewBox="0 0 12 12" className="external-icon">
                                                                        <path d="M3.5 3H8.5V8M8.5 3L3.5 8" stroke="currentColor" strokeWidth="1.5" fill="none" />
                                                                    </svg>
                                                                </div>
                                                                {link.description && (
                                                                    <p className="link-description">{link.description}</p>
                                                                )}
                                                            </div>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                <div className="content-section" >
                    <h2>Education</h2>
                    <div className="experience-item">
                        <div className="experience-content">
                            <div className="company-logo">
                                <img src="/images/ATU.png" alt="ATU Logo" />
                            </div>
                            <div className="experience-info">
                                <h3>Bachelor of Engineering in Computer Engineering</h3>
                                <p className="company-location">Atlantic Technological University • Galway, Ireland</p>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Experience;